import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { useUser } from '@/lib/hooks'
import ExerciseCalculator, { ExerciseResults } from '@/components/exercise-calculator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Calculator,
  Download,
  History,
  Clock,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Calendar,
  FileText,
  ChevronRight
} from 'lucide-react'
import { format, parseISO, differenceInDays, addYears } from 'date-fns'
import { toast } from 'sonner'

interface OptionGrant {
  id: string
  user_id: string
  shares_offered: number
  shares_exercised?: number
  strike_price: number
  grant_date: string | null
  vesting_start_date: string | null
  expiration_date?: string | null
  option_type: 'ISO' | 'NSO'
  status: string
  users?: {
    id: string
    email: string
    first_name?: string
    last_name?: string
  } | null
  vesting_schedules?: {
    schedule_name: string
    duration_months: number
    cliff_months: number
  } | null
}

interface OptionExercise {
  id: string
  option_grant_id: string
  exercise_date: string
  shares_exercised: number
  exercise_price: number
  fair_market_value: number
  exercise_type: 'cash' | 'cashless'
  total_cost: number
  taxes_withheld?: number
  shares_withheld?: number
  net_shares_received?: number
  notes?: string
  created_at: string
}

export default function OptionExercisesPage() {
  const getToken = useGetToken()
  const { currentProject } = useProject()
  const { profile } = useUser()
  const queryClient = useQueryClient()
  const [selectedGrant, setSelectedGrant] = useState<OptionGrant | null>(null)
  const [showExerciseDialog, setShowExerciseDialog] = useState(false)
  const [exerciseResults, setExerciseResults] = useState<ExerciseResults | null>(null)
  const [exerciseNotes, setExerciseNotes] = useState('')

  // Fetch option grants
  const { data: optionGrants, isLoading } = useQuery({
    queryKey: ['option-grants', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('equity_offers')
        .select(`
          *,
          users (id, email, first_name, last_name),
          vesting_schedules (schedule_name, duration_months, cliff_months)
        `)
        .eq('project_id', currentProject.id)
        .eq('equity_type', 'options')
        .eq('status', 'accepted')
        .order('grant_date', { ascending: false })

      if (error) throw error
      return data as unknown as OptionGrant[]
    },
    enabled: !!currentProject
  })

  // Fetch exercise history
  const { data: exerciseHistory } = useQuery({
    queryKey: ['exercise-history', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // For now, we'll simulate this data since we don't have an exercises table
      // In a real implementation, you'd create an option_exercises table
      return [] as OptionExercise[]
    },
    enabled: !!currentProject
  })

  // Get current FMV
  const { data: currentFMV } = useQuery({
    queryKey: ['current-fmv', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return 0

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('valuations')
        .select('common_share_price')
        .eq('project_id', currentProject.id)
        .order('valuation_date', { ascending: false })
        .limit(1)
        .single()

      if (error) return 1.00 // Default FMV
      return data.common_share_price || 1.00
    },
    enabled: !!currentProject
  })

  // Record exercise
  const { mutate: recordExercise, isPending: isRecording } = useMutation({
    mutationFn: async (data: {
      grantId: string
      results: ExerciseResults
      notes: string
    }) => {
      if (!currentProject || !profile) throw new Error('No project or user')

      // In a real implementation, you'd insert into an option_exercises table
      // For now, we'll update the share ledger
      const { error } = await supabase
        .from('share_ledger')
        .insert({
          project_id: currentProject.id,
          transaction_date: new Date().toISOString().split('T')[0],
          transaction_type: 'option_exercise',
          user_id: selectedGrant?.user_id,
          equity_class_id: '', // TODO: Set to actual common stock equity class ID
          shares_amount: data.results.sharesToExercise,
          price_per_share: data.results.exerciseCost / data.results.sharesToExercise,
          total_consideration: data.results.exerciseCost,
          notes: `Option exercise: ${data.results.sharesToExercise} shares at $${data.results.exerciseCost / data.results.sharesToExercise} per share. ${data.notes}`,
          created_by: profile.id
        })

      if (error) throw error

      // Update the grant to track exercised shares
      // This would be done properly with a dedicated exercises table
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-grants'] })
      queryClient.invalidateQueries({ queryKey: ['share-ledger'] })
      setShowExerciseDialog(false)
      setSelectedGrant(null)
      setExerciseResults(null)
      setExerciseNotes('')
      toast.success('Option exercise recorded successfully')
    },
    onError: () => {
      toast.error('Failed to record option exercise')
    }
  })

  const handleExportReport = () => {
    if (!optionGrants || optionGrants.length === 0) return

    const headers = [
      'Employee',
      'Grant Date',
      'Option Type',
      'Total Granted',
      'Strike Price',
      'Vested',
      'Exercised',
      'Available',
      'Expiration',
      'Days to Expiration',
      'Current FMV',
      'In the Money'
    ]

    const rows = optionGrants.map(grant => {
      const userName = grant.users ? 
        `${grant.users.first_name || ''} ${grant.users.last_name || ''}`.trim() || grant.users.email : 
        'Unknown'
      const exercised = grant.shares_exercised || 0
      const vested = calculateVestedShares(grant)
      const available = Math.min(vested - exercised, grant.shares_offered - exercised)
      const grantDateStr = grant.grant_date || new Date().toISOString()
      const expirationDate = grant.expiration_date ||
        format(addYears(parseISO(grantDateStr), 10), 'yyyy-MM-dd')
      const daysToExpiration = differenceInDays(parseISO(expirationDate), new Date())
      const inTheMoney = (currentFMV || 0) > grant.strike_price

      return [
        userName,
        format(parseISO(grantDateStr), 'yyyy-MM-dd'),
        grant.option_type,
        grant.shares_offered,
        grant.strike_price,
        vested,
        exercised,
        available,
        expirationDate,
        daysToExpiration,
        currentFMV || 0,
        inTheMoney ? 'Yes' : 'No'
      ]
    })

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `option-exercise-report-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const calculateVestedShares = (grant: OptionGrant): number => {
    if (!grant.vesting_schedules) return grant.shares_offered

    const today = new Date()
    const vestingStart = parseISO(grant.vesting_start_date || grant.grant_date || new Date().toISOString())
    const monthsElapsed = Math.floor(
      (today.getTime() - vestingStart.getTime()) / (1000 * 60 * 60 * 24 * 30)
    )

    if (monthsElapsed < grant.vesting_schedules.cliff_months) {
      return 0
    }

    const vestedMonths = Math.min(monthsElapsed, grant.vesting_schedules.duration_months)
    return Math.floor((grant.shares_offered / grant.vesting_schedules.duration_months) * vestedMonths)
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num))
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Option Exercise Tracking</h1>
          <p className="mt-1 text-sm text-gray-600">
            Calculate exercise costs and track option exercises
          </p>
        </div>
        <Button variant="outline" onClick={handleExportReport}>
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Options Granted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatNumber(optionGrants?.reduce((sum, g) => sum + g.shares_offered, 0) || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Vested & Exercisable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              {formatNumber(
                optionGrants?.reduce((sum, g) => sum + calculateVestedShares(g) - (g.shares_exercised || 0), 0) || 0
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Already Exercised
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600">
              {formatNumber(optionGrants?.reduce((sum, g) => sum + (g.shares_exercised || 0), 0) || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Current FMV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(currentFMV || 0)}</p>
            <p className="text-xs text-gray-500">per share</p>
          </CardContent>
        </Card>
      </div>

      {/* Option Grants Table */}
      <Card>
        <CardHeader>
          <CardTitle>Outstanding Option Grants</CardTitle>
          <CardDescription>
            Click on a grant to calculate exercise costs and tax implications
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : optionGrants && optionGrants.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Holder</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Granted</TableHead>
                    <TableHead>Strike Price</TableHead>
                    <TableHead>Vested</TableHead>
                    <TableHead>Available</TableHead>
                    <TableHead>Expiration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {optionGrants.map((grant) => {
                    const vested = calculateVestedShares(grant)
                    const exercised = grant.shares_exercised || 0
                    const available = Math.min(vested - exercised, grant.shares_offered - exercised)
                    const expirationDate = grant.expiration_date ||
                      addYears(parseISO(grant.grant_date || new Date().toISOString()), 10)
                    const daysToExpiration = differenceInDays(expirationDate, new Date())
                    const isExpiringSoon = daysToExpiration < 90 && daysToExpiration > 0
                    const isExpired = daysToExpiration <= 0
                    const inTheMoney = (currentFMV || 0) > grant.strike_price

                    return (
                      <TableRow 
                        key={grant.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setSelectedGrant(grant)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {grant.users ? 
                                `${grant.users.first_name || ''} ${grant.users.last_name || ''}`.trim() || grant.users.email : 
                                'Unknown'}
                            </p>
                            <p className="text-sm text-gray-500">{grant.users?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={grant.option_type === 'ISO' ? 'default' : 'secondary'}>
                            {grant.option_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p>{formatNumber(grant.shares_offered)}</p>
                            <p className="text-xs text-gray-500">
                              {grant.grant_date ? format(parseISO(grant.grant_date), 'MMM d, yyyy') : '-'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{formatCurrency(grant.strike_price)}</TableCell>
                        <TableCell>
                          <div>
                            <p>{formatNumber(vested)}</p>
                            {exercised > 0 && (
                              <p className="text-xs text-gray-500">
                                {formatNumber(exercised)} exercised
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className={available > 0 ? 'font-medium' : 'text-gray-400'}>
                            {formatNumber(available)}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className={isExpiringSoon ? 'text-orange-600 font-medium' : ''}>
                              {format(expirationDate, 'MMM d, yyyy')}
                            </p>
                            {!isExpired && (
                              <p className={`text-xs ${isExpiringSoon ? 'text-orange-600' : 'text-gray-500'}`}>
                                {daysToExpiration} days
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isExpired ? (
                            <Badge variant="destructive">Expired</Badge>
                          ) : inTheMoney ? (
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              ITM
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              OTM
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No option grants found</p>
              <p className="text-sm text-gray-400 mt-1">
                Option grants will appear here once created
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exercise History */}
      {exerciseHistory && exerciseHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Exercise History</CardTitle>
            <CardDescription>
              Past option exercises and their details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Shares</TableHead>
                    <TableHead>Exercise Type</TableHead>
                    <TableHead>Strike Price</TableHead>
                    <TableHead>FMV</TableHead>
                    <TableHead>Total Cost</TableHead>
                    <TableHead>Net Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exerciseHistory.map((exercise) => (
                    <TableRow key={exercise.id}>
                      <TableCell>
                        {format(parseISO(exercise.exercise_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>{formatNumber(exercise.shares_exercised)}</TableCell>
                      <TableCell>
                        <Badge variant={exercise.exercise_type === 'cash' ? 'default' : 'secondary'}>
                          {exercise.exercise_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(exercise.exercise_price)}</TableCell>
                      <TableCell>{formatCurrency(exercise.fair_market_value)}</TableCell>
                      <TableCell>{formatCurrency(exercise.total_cost)}</TableCell>
                      <TableCell>
                        {exercise.net_shares_received 
                          ? formatNumber(exercise.net_shares_received) 
                          : formatNumber(exercise.shares_exercised)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exercise Dialog */}
      {selectedGrant && (
        <Dialog open={!!selectedGrant} onOpenChange={() => setSelectedGrant(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Exercise Options</DialogTitle>
              <DialogDescription>
                Calculate costs and tax implications for exercising options
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {/* Grant Details */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-base">Option Grant Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-gray-500">Holder</dt>
                      <dd className="font-medium">
                        {selectedGrant.users ? 
                          `${selectedGrant.users.first_name || ''} ${selectedGrant.users.last_name || ''}`.trim() || selectedGrant.users.email : 
                          'Unknown'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Grant Date</dt>
                      <dd className="font-medium">
                        {selectedGrant.grant_date ? format(parseISO(selectedGrant.grant_date), 'MMMM d, yyyy') : '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Total Granted</dt>
                      <dd className="font-medium">{formatNumber(selectedGrant.shares_offered)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Vested & Available</dt>
                      <dd className="font-medium">
                        {formatNumber(
                          Math.min(
                            calculateVestedShares(selectedGrant) - (selectedGrant.shares_exercised || 0),
                            selectedGrant.shares_offered - (selectedGrant.shares_exercised || 0)
                          )
                        )}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              {/* Exercise Calculator */}
              <ExerciseCalculator
                optionType={selectedGrant.option_type}
                sharesAvailable={Math.min(
                  calculateVestedShares(selectedGrant) - (selectedGrant.shares_exercised || 0),
                  selectedGrant.shares_offered - (selectedGrant.shares_exercised || 0)
                )}
                strikePrice={selectedGrant.strike_price}
                currentFMV={currentFMV || 1.00}
                onCalculate={setExerciseResults}
              />

              {/* Notes */}
              {exerciseResults && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-base">Exercise Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={exerciseNotes}
                      onChange={(e) => setExerciseNotes(e.target.value)}
                      placeholder="Add any notes about this exercise (optional)"
                      rows={3}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedGrant(null)}>
                Cancel
              </Button>
              {exerciseResults && (
                <Button
                  onClick={() => setShowExerciseDialog(true)}
                  disabled={!exerciseResults.sharesToExercise}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Record Exercise
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm Exercise Dialog */}
      <Dialog open={showExerciseDialog} onOpenChange={setShowExerciseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Option Exercise</DialogTitle>
            <DialogDescription>
              Please review and confirm the exercise details
            </DialogDescription>
          </DialogHeader>

          {exerciseResults && selectedGrant && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Shares to Exercise</span>
                  <span className="font-medium">{formatNumber(exerciseResults.sharesToExercise)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Exercise Cost</span>
                  <span className="font-medium">{formatCurrency(exerciseResults.exerciseCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Tax Withholding</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(
                      selectedGrant.option_type === 'ISO' && exerciseResults.netProceeds < 0
                        ? Math.abs(exerciseResults.netProceeds + exerciseResults.exerciseCost)
                        : exerciseResults.ordinaryIncome * 0.37
                    )}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-sm font-medium">Net Proceeds</span>
                  <span className="font-bold text-lg">
                    {formatCurrency(exerciseResults.netProceeds)}
                  </span>
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This will record the option exercise in the share ledger. Make sure you have 
                  completed the actual exercise with your company's stock plan administrator.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExerciseDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedGrant && exerciseResults) {
                  recordExercise({
                    grantId: selectedGrant.id,
                    results: exerciseResults,
                    notes: exerciseNotes
                  })
                }
              }}
              disabled={isRecording}
            >
              {isRecording ? 'Recording...' : 'Confirm Exercise'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
